import {Country} from '@hconnect/apiclient'
import {flagPathByCountryCode} from '@hconnect/uikit'
import {TextField} from '@material-ui/core'
import React from 'react'
import {Controller, UseFormReturn} from 'react-hook-form'

import {CountriesDropdown} from '../../../components/CountriesDropdown/deprecated/CountriesDropdown'
import {useStyles} from '../../../components/UserInformation/inputs/UserInput.styles'
import {UserInformationInputSkeleton} from '../../../components/UserInformation/Skeleton/UserInformationInputSkeleton'
import {useTranslation} from '../../../hooks/useTranslation'

import {ExportUserFormData} from './ExportUserDataDialog'

type ExportUserCountryInputProps = {
  formMethods: UseFormReturn<ExportUserFormData, object>
  isLoading: boolean
  readonlyMode?: boolean
  countriesIds: string[]
  selectedCountry?: Country
}

export const ExportUserCountryInput = ({
  readonlyMode,
  formMethods,
  countriesIds,
  isLoading,
  selectedCountry
}: ExportUserCountryInputProps) => {
  const {t} = useTranslation()
  const errors = formMethods.formState.errors
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const touched = formMethods.formState.touchedFields
  const classes = useStyles()
  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const countryDataFilter = (countryId: string) =>
    countryId === 'MOCK'
      ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
      : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

  const formattedCountryData = countriesIds
    .filter((countryId) => countryDataFilter(countryId))
    .map((countryId) => {
      return {
        label: countryDataFilter(countryId)?.name ?? '',
        value: countryDataFilter(countryId)?.iso2.toUpperCase() ?? ''
      }
    })
    .sort((current, next) => {
      if (current.label < next.label) {
        return -1
      }
      if (current.label > next.label) {
        return 1
      }
      return 0
    })

  return (
    <>
      {countriesIds.length === 1 ? (
        <TextField
          label={t('userInformationForm.country')}
          data-test-id="export-user-data-countryId"
          value={
            formattedCountryData.find((countryData) => countryData.value === countriesIds[0])
              ?.label || selectedCountry?.countryName
          }
          InputProps={{
            readOnly: true,
            disableUnderline: true,
            ...(selectedCountry?.countryCode !== 'MOCK' && {
              startAdornment: (
                <img
                  src={flagPathByCountryCode(countriesIds[0])}
                  alt={`${selectedCountry?.countryCode}-flag`}
                  style={{marginRight: '12px'}}
                />
              )
            })
          }}
          InputLabelProps={{shrink: true}}
          margin="normal"
          fullWidth
        />
      ) : (
        countriesIds.length > 1 && (
          <Controller
            name="countryId"
            control={formMethods.control}
            rules={{
              validate: (value) => !Array.isArray(value) || !value,
              required: true
            }}
            render={({field}) => (
              <CountriesDropdown
                {...field}
                value={field.value}
                data-test-id="export-user-data-countryId"
                select={!readonlyMode}
                className={classes.textField}
                fullWidth
                label={t('userInformationForm.country')}
                SelectProps={{
                  MenuProps: {
                    style: {
                      height: '400px'
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    getContentAnchorEl: null
                  }
                }}
                margin="normal"
                error={!!errors.countryId}
                helperText={!!errors.countryId && 'Please select a country'}
                InputProps={{
                  readOnly: readonlyMode,
                  disableUnderline: readonlyMode
                }}
                countriesList={formattedCountryData}
                defaultSelectTitle={t('userInformationForm.select')}
              />
            )}
          />
        )
      )}
    </>
  )
}
